import {
  OktonSearchSelect,
  OktonInput,
  OktonAlert,
  OktonSelect,
  OktonPageHeader,
  OktonDataGrid,
} from '@okton/platform-ui';
import {
  Badge,
  Box,
  Button,
  Flex,
  Spinner,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Check, FileText, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import BankAppPage from '../../../layout/BankAppPage';

import {
  parseOFX,
  decodificarOFX,
  OFXStatement,
} from '../../../utils/ofxParser';
import formatValue from '../../../utils/formatValue';
import { formatCustomDate } from '../../../utils/formatCustomDate';
import { useImportarOfx } from '../../../services/hooks/useExtratoBancarioMutations';
import { useContasBancarias } from '../../../services/hooks/useContasBancarias';
import { contaCombina, bancoDivergente } from '../../../utils/ofxConta';

type StepState = 'select-file' | 'preview' | 'done';

// Campos no vocabulário que o endpoint de importação espera.
interface LancamentoImportado {
  data: string;
  // HH:mm:ss, ou vazio quando o arquivo não traz a hora.
  hora: string;
  descricao: string;
  valor: number;
  referencia: string;
  // FITID como o banco mandou. Não deduplica nada — é guardado só para
  // reencontrar o lançamento no extrato do banco depois.
  fitid: string;
}

interface ImportResult {
  inseridos: number;
  ignorados: number;
  descartados: number;
  // Dos que já existiam, quantos estavam sem hora e ganharam a do arquivo.
  horaspreenchidas?: number;
}

/**
 * Importação de extrato OFX, em rota própria como as demais telas.
 *
 * O arquivo pode trazer mais de uma conta: cada extrato é importado à parte,
 * e a conta do IFLEX é adivinhada pelo número que veio no arquivo.
 */
export default function ImportarOFX() {
  const history = useHistory();
  const { data: accounts = [] } = useContasBancarias();
  const fileRef = useRef<HTMLInputElement>(null);

  // estilos
  const color = useColorModeValue('#3f3f46', '#d4d4d8');
  const bg = useColorModeValue('white', 'gray.800');
  const subtleBg = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textMuted = useColorModeValue('gray.500', 'gray.400');
  const selecionado = useColorModeValue(
    'rgba(0, 130, 250, 0.15)',
    'rgba(0,50,200,0.1)',
  );

  // estados
  const [step, setStep] = useState<StepState>('select-file');
  const [fileName, setFileName] = useState('');
  // Um arquivo pode trazer mais de uma conta. Antes tudo virava uma lista só e
  // ia para a conta escolhida na tela; agora cada extrato é importado à parte.
  const [statements, setStatements] = useState<OFXStatement[]>([]);
  const [indiceExtrato, setIndiceExtrato] = useState(0);
  const [accountId, setAccountId] = useState<string>(
    accounts[0] ? accounts[0].id_cts : '',
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [parseError, setParseError] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const importarOfx = useImportarOfx();

  const extrato: OFXStatement | undefined = statements[indiceExtrato];
  const parsedTxs = extrato?.transactions ?? [];
  const contaSelecionada = accounts.find(a => a.id_cts === accountId);
  const divergencia = bancoDivergente(
    contaSelecionada,
    extrato?.bankId ?? null,
  );

  function resetImportacao() {
    setStep('select-file');
    setFileName('');
    setStatements([]);
    setIndiceExtrato(0);
    setAccountId(accounts[0] ? accounts[0].id_cts : '');
    setSelected(new Set());
    setParseError('');
    setImportResult(null);
    setDragOver(false);
  }

  // Sair da tela é voltar para a conciliação: a importação já invalidou as
  // listas, então ela recarrega sozinha com o que entrou.
  function handleClose() {
    resetImportacao();
    history.push('/conciliacao');
  }

  // Seleciona o extrato e, quando dá, a conta correspondente no IFLEX.
  function escolherExtrato(lista: OFXStatement[], indice: number) {
    setIndiceExtrato(indice);
    const alvo = lista[indice];
    setSelected(new Set(alvo.transactions.map(t => t.referencia)));
    const match = accounts.find(a => contaCombina(a, alvo.accountId));
    if (match) setAccountId(match.id_cts);
  }

  function handleFile(file: File) {
    setParseError('');
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => {
      try {
        // Lido como bytes, não como texto: o encoding é decidido pelo conteúdo
        // do arquivo, não fixado em latin1 como antes — era isso que corrompia
        // todo OFX em UTF-8 ("Antecipação" virava "AntecipaÃ§Ã£o").
        const dados = parseOFX(decodificarOFX(e.target?.result as ArrayBuffer));
        setStatements(dados.statements);
        escolherExtrato(dados.statements, 0);
        setStep('preview');
      } catch (err: unknown) {
        setParseError(
          err instanceof Error
            ? err.message
            : 'Erro ao processar o arquivo OFX.',
        );
      }
    };
    reader.readAsArrayBuffer(file);
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }

  function toggleTx(referencia: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(referencia)) next.delete(referencia);
      else next.add(referencia);
      return next;
    });
  }

  async function doImport() {
    if (!accountId) {
      setParseError('Selecione a conta bancária de destino.');
      return;
    }
    if (selected.size === 0) {
      setParseError('Selecione ao menos uma transação para importar.');
      return;
    }
    // Barra antes de gravar: com a conta errada a unique não pega nada (o
    // conta_exb é outro) e o extrato de um banco entraria inteiro no outro.
    if (divergencia) {
      setParseError(
        `O arquivo é do banco ${extrato?.bankId} e a conta selecionada é de outro banco. Escolha a conta correta.`,
      );
      return;
    }
    setParseError('');
    setImporting(true);

    const lancamentos: LancamentoImportado[] = parsedTxs
      .filter(t => selected.has(t.referencia))
      .map(t => ({
        data: t.date,
        hora: t.time ?? '',
        descricao: t.memo,
        valor: t.amount,
        referencia: t.referencia,
        fitid: t.fitId ?? '',
      }));

    try {
      // A deduplicação é do banco: a unique (filial, conta, referência) faz o
      // INSERT IGNORE descartar o que já entrou numa importação anterior.
      const resultado = await importarOfx.mutateAsync({
        conta: accountId,
        lancamentos,
      });
      setImportResult(resultado);
      setStep('done');
    } catch (err: unknown) {
      setParseError(
        err instanceof Error ? err.message : 'Erro ao importar transações.',
      );
    } finally {
      setImporting(false);
    }
  }

  const totalCredit = parsedTxs
    .filter(t => selected.has(t.referencia) && t.amount >= 0)
    .reduce((s, t) => s + t.amount, 0);
  const totalDebit = parsedTxs
    .filter(t => selected.has(t.referencia) && t.amount < 0)
    .reduce((s, t) => s + t.amount, 0);

  const stepLabels: Record<StepState, string> = {
    'select-file': '1. Arquivo',
    preview: '2. Revisão',
    done: '3. Concluído',
  };
  const stepOrder: StepState[] = ['select-file', 'preview', 'done'];

  function avisoBox(texto: string, tom: 'red' | 'orange') {
    return (
      <OktonAlert
        status={tom === 'red' ? 'error' : 'warning'}
        description={texto}
      />
    );
  }

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title="Importar Extrato OFX"
        subtitle="Arquivo do banco, conferido antes de entrar no extrato"
        onBack={() => history.push('/conciliacao')}
      />

      <Box
        bg={bg}
        mt="4"
        mb="6"
        borderWidth="1px"
        rounded="2xl"
        overflow="hidden"
      >
        {/* Em que passo a importação está */}
        <Flex
          align="center"
          gap="2"
          px="6"
          py="3"
          borderBottomWidth="1px"
          borderColor={borderColor}
          fontSize="xs"
          fontWeight="medium"
        >
          <FileText size={16} color="#2563eb" />
          {stepOrder.map((s, i) => (
            <Flex key={s} align="center" gap="2">
              {i > 0 && <Text color="gray.300">/</Text>}
              <Text
                color={
                  step === s
                    ? 'blue.500'
                    : stepOrder.indexOf(step) > i
                    ? 'green.500'
                    : 'gray.400'
                }
              >
                {stepLabels[s]}
              </Text>
            </Flex>
          ))}
        </Flex>

        <Box>
          {/* STEP 1: seleção de arquivo */}
          {step === 'select-file' && (
            <Flex direction="column" gap="4" p="6">
              <Box
                onDragOver={e => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => fileRef.current?.click()}
                borderWidth="2px"
                borderStyle="dashed"
                borderColor={dragOver ? 'blue.400' : borderColor}
                bg={dragOver ? 'blue.50' : subtleBg}
                rounded="xl"
                p="12"
                cursor="pointer"
                _hover={{ borderColor: 'blue.300', bg: subtleBg }}
                transition="all 0.2s"
              >
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  gap="2"
                >
                  <Upload size={40} color={dragOver ? '#3b82f6' : '#d1d5db'} />
                  <Text fontSize="sm" fontWeight="medium">
                    Arraste um arquivo OFX ou clique para selecionar
                  </Text>
                  <Text fontSize="xs" color={textMuted}>
                    Formatos suportados: .ofx (OFX 1.x SGML e OFX 2.x XML)
                  </Text>
                </Flex>
                <Box display="none">
                  <OktonInput
                    name="importar-ofx-arquivo"
                    aria-label="Arquivo OFX"
                    ref={fileRef}
                    type="file"
                    accept=".ofx,.OFX"
                    style={{ display: 'none' }}
                    onChange={onInputChange}
                  />
                </Box>
              </Box>

              {parseError && avisoBox(parseError, 'red')}

              <Box
                bg={selecionado}
                rounded="xl"
                px="4"
                py="3"
                fontSize="xs"
                color="blue.700"
              >
                <Text fontWeight="semibold" mb="1">
                  Como exportar o arquivo OFX do seu banco:
                </Text>
                <Text>
                  Acesse o Internet Banking, vá em Extratos, selecione o período
                  e exporte no formato OFX ou Open Finance.
                </Text>
                <Text mt="1">
                  Os principais bancos brasileiros (Bradesco, Itaú, BB,
                  Santander, Caixa, Nubank) suportam este formato.
                </Text>
              </Box>
            </Flex>
          )}

          {/* STEP 2: revisão */}
          {step === 'preview' && extrato && (
            <Flex direction="column" h="100%">
              <Flex
                direction="column"
                gap="3"
                px="6"
                pt="4"
                pb="3"
                flexShrink={0}
              >
                {fileName && (
                  <Flex
                    align="center"
                    gap="2"
                    fontSize="sm"
                    color="gray.600"
                    bg={subtleBg}
                    rounded="lg"
                    px="3"
                    py="2"
                    flexWrap="wrap"
                  >
                    <FileText
                      size={16}
                      color="#3b82f6"
                      style={{ flexShrink: 0 }}
                    />
                    <Text fontWeight="medium" isTruncated px="1">
                      {fileName}
                    </Text>
                    {extrato.bankId && (
                      <Text color="gray.400">• Banco: {extrato.bankId}</Text>
                    )}
                    {extrato.accountId && (
                      <Text color="gray.400">• Conta: {extrato.accountId}</Text>
                    )}
                    {extrato.periodStart && extrato.periodEnd && (
                      <Text color="gray.400">
                        • Período:{' '}
                        {formatCustomDate(
                          extrato.periodStart,
                          'yyyy-MM-dd',
                          'dd/MM/yyyy',
                        )}{' '}
                        a{' '}
                        {formatCustomDate(
                          extrato.periodEnd,
                          'yyyy-MM-dd',
                          'dd/MM/yyyy',
                        )}
                      </Text>
                    )}
                    {extrato.balance !== null && (
                      <Text color="gray.400">
                        • Saldo no arquivo: {formatValue(extrato.balance)}
                      </Text>
                    )}
                  </Flex>
                )}

                {/* Arquivo com mais de uma conta: um extrato por vez */}
                {statements.length > 1 && (
                  <Flex align="center" gap="2">
                    <Text
                      as="label"
                      htmlFor="extratoArquivo"
                      display="block"
                      fontSize="sm"
                      fontWeight="medium"
                      whiteSpace="nowrap"
                    >
                      Extrato do arquivo:
                    </Text>
                    <OktonSelect
                      size="lg"
                      rounded="2xl"
                      name="extratoArquivo"
                      m="2"
                      bg={bg}
                      flex="1"
                      value={String(indiceExtrato)}
                      onChange={e =>
                        escolherExtrato(statements, Number(e.target.value))
                      }
                      borderWidth="2px"
                    >
                      {statements.map((s, i) => (
                        // A referência do primeiro lançamento identifica o
                        // extrato sem depender da posição na lista.
                        <option
                          key={`${s.accountId ?? 'conta'}-${
                            s.transactions[0]?.referencia ?? i
                          }`}
                          value={i}
                        >
                          {`Banco ${s.bankId ?? '—'} · Conta ${
                            s.accountId ?? '—'
                          } · ${s.transactions.length} lançamento(s)`}
                        </option>
                      ))}
                    </OktonSelect>
                  </Flex>
                )}

                <Flex align="center" gap="3" flexWrap="wrap">
                  <Flex align="center" gap="2" minW="320px">
                    <Text
                      as="label"
                      htmlFor="contaofx"
                      display="block"
                      fontSize="sm"
                      fontWeight="medium"
                      whiteSpace="nowrap"
                    >
                      Importar para:
                    </Text>
                    <Box flex="1" m="2">
                      <OktonSearchSelect
                        name="contaofx"
                        placeholder="Selecione a conta"
                        value={accountId}
                        onChange={valor => setAccountId(valor)}
                        options={accounts.map(a => ({
                          value: a.id_cts,
                          label: `${a.descricao_cts} — ${
                            a.nome_ban ?? 'Sem banco'
                          }`,
                        }))}
                      />
                    </Box>
                  </Flex>
                  <Flex fontSize="xs" ml="4">
                    <Badge
                      colorScheme="green"
                      borderRadius="lg"
                      px="2"
                      py="1"
                      mx="2"
                    >
                      {formatValue(totalCredit)}
                    </Badge>
                    <Badge
                      colorScheme="red"
                      borderRadius="lg"
                      px="2"
                      py="1"
                      mx="2"
                    >
                      {formatValue(totalDebit)}
                    </Badge>
                    <Badge
                      colorScheme="blue"
                      borderRadius="lg"
                      px="2"
                      py="1"
                      mx="2"
                    >
                      {selected.size}/{parsedTxs.length} selecionados
                    </Badge>
                  </Flex>
                  {/* As ações ficam na mesma linha da Conta: no rodapé da tela,
                  com um arquivo de muitos lançamentos, era preciso rolar a lista
                  inteira para chegar ao botão de importar. */}
                  <Flex align="center" gap="2" ml="auto" gridGap={2}>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setStep('select-file');
                        setParseError('');
                      }}
                      borderWidth="2px"
                    >
                      Trocar arquivo
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClose}
                      borderWidth="2px"
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      leftIcon={
                        importing ? <Spinner size="sm" /> : <Upload size={16} />
                      }
                      colorScheme="blue"
                      isDisabled={
                        importing ||
                        selected.size === 0 ||
                        !accountId ||
                        divergencia
                      }
                      onClick={doImport}
                    >
                      Importar {selected.size} transação(ões)
                    </Button>
                  </Flex>
                </Flex>

                {divergencia &&
                  avisoBox(
                    `O arquivo é do banco ${
                      extrato.bankId
                    } e a conta selecionada é de outro banco (${
                      contaSelecionada?.nome_ban ?? 'sem banco'
                    }). Confira a conta antes de importar.`,
                    'red',
                  )}

                {extrato.semData > 0 &&
                  avisoBox(
                    `${extrato.semData} lançamento(s) do arquivo vieram sem data ou sem valor válido e não serão importados.`,
                    'orange',
                  )}

                {parseError && avisoBox(parseError, 'red')}
              </Flex>

              {/* Tabela de transações: clicar na linha também marca */}
              <Box overflowY="auto" px="6" pb="4">
                <OktonDataGrid
                  rows={parsedTxs}
                  columns={[
                    {
                      key: 'data',
                      header: 'Data',
                      render: tx => (
                        <Box color="gray.600" whiteSpace="nowrap">
                          {formatCustomDate(
                            tx.date,
                            'yyyy-MM-dd',
                            'dd/MM/yyyy',
                          )}
                          {tx.time && ` ${tx.time.slice(0, 5)}`}
                        </Box>
                      ),
                    },
                    {
                      key: 'descricao',
                      header: 'Descrição',
                      render: tx => (
                        <Box
                          maxW="xs"
                          isTruncated
                          color={color}
                          fontWeight="bold"
                        >
                          {tx.memo}
                        </Box>
                      ),
                    },
                    {
                      key: 'referencia',
                      header: 'Referência',
                      // O FITID do banco fica no title: ele não é confiável
                      // como chave (BB repete, Sicredi renumera), mas ajuda a
                      // achar o lançamento no extrato do banco.
                      render: tx => (
                        <Box
                          color="gray.400"
                          fontSize="xs"
                          fontFamily="mono"
                          maxW="100px"
                          isTruncated
                          title={tx.fitId ? `FITID do banco: ${tx.fitId}` : ''}
                        >
                          {tx.referencia}
                        </Box>
                      ),
                    },
                    {
                      key: 'valor',
                      header: 'Valor',
                      align: 'right',
                      render: tx => (
                        <Box
                          fontWeight="semibold"
                          whiteSpace="nowrap"
                          color={tx.amount >= 0 ? 'green.600' : 'red.500'}
                        >
                          {formatValue(tx.amount)}
                        </Box>
                      ),
                    },
                  ]}
                  getRowId={tx => tx.referencia}
                  onRowClick={tx => toggleTx(tx.referencia)}
                  selectable
                  selectedIds={Array.from(selected)}
                  onSelectionChange={ids =>
                    setSelected(new Set(ids.map(String)))
                  }
                  hidePagination
                />
              </Box>
            </Flex>
          )}

          {/* STEP 3: concluído */}
          {step === 'done' && importResult && (
            <Flex
              direction="column"
              align="center"
              justify="center"
              py="12"
              px="8"
              gap="4"
              textAlign="center"
            >
              <Flex
                bg={importResult.inseridos > 0 ? 'green.50' : 'red.50'}
                p="4"
                borderRadius="full"
              >
                {importResult.inseridos > 0 ? (
                  <Check size={40} color="#059669" />
                ) : (
                  <X size={40} color="#dc2626" />
                )}
              </Flex>
              <Box>
                <Text fontSize="lg" fontWeight="semibold">
                  {importResult.inseridos > 0
                    ? 'Importação concluída!'
                    : 'Importação recusada'}
                </Text>
                <Text fontSize="sm" color={textMuted} mt="1">
                  {importResult.inseridos > 0
                    ? `${importResult.inseridos} transação(ões) importada(s) com sucesso.`
                    : 'Nenhuma transação nova encontrada.'}
                  {importResult.ignorados > 0 &&
                    ` ${importResult.ignorados} já existia(m) e foi(ram) ignorada(s).`}
                  {!!importResult.horaspreenchidas &&
                    ` ${importResult.horaspreenchidas} já existente(s) ganhou(aram) a hora do arquivo.`}
                  {importResult.descartados > 0 &&
                    ` ${importResult.descartados} foi(ram) descartada(s) por dado inválido.`}
                </Text>
              </Box>
              {importResult.inseridos === 0 && importResult.ignorados > 0 && (
                <Box
                  bg="orange.50"
                  borderWidth="1px"
                  borderColor="orange.200"
                  rounded="xl"
                  px="4"
                  py="3"
                  fontSize="sm"
                  color="orange.700"
                  maxW="sm"
                >
                  Todas as transações selecionadas já foram importadas
                  anteriormente.
                </Box>
              )}
              <Button colorScheme="blue" mt="2" onClick={handleClose}>
                Fechar
              </Button>
            </Flex>
          )}
        </Box>

        {/* Rodapé — só aparece no passo de revisão */}
      </Box>
    </BankAppPage>
  );
}
